import { FormikValues } from "formik";

import { useCourts } from "@/common/hooks/useCourts";
import { useTranslation } from "@/translations";

import {
  FormikAutocompleteSelectProps,
  IOptionItem,
} from "../fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "../fields/formik/formik-autocomplete/FormikAutocompleteSelect";

/**
 * The stored value of the "Alle Gerichte" entry offered with `includeAllOption`. Not a court id, so
 * callers translate it into "no court filter" before it reaches an endpoint.
 */
export const ALL_COURTS_OPTION_ID = "all";

/**
 * `Omit` over a union collapses it into one object type. The field props are a union — either
 * `path` or `fieldName`, never both — so a plain `Omit` would make both optional and satisfy
 * neither branch. Distributing keeps the discrimination intact.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type FormikCourtSelectProps<T extends FormikValues> = DistributiveOmit<FormikAutocompleteSelectProps<T>, "options"> & {
  /** Offers "Alle Gerichte" ({@link ALL_COURTS_OPTION_ID}) ahead of the courts. */
  includeAllOption?: boolean;
};

/** A dropdown of the configured courts (Gerichte), sorted by name. */
export function FormikCourtSelect<T extends FormikValues>(props: FormikCourtSelectProps<T>) {
  const { includeAllOption = false, ...rest } = props;
  const { t } = useTranslation();
  const { courts } = useCourts();

  const courtOptions: IOptionItem[] = courts
    .map((court) => ({ id: court.id, label: court.name }))
    .sort((a, b) => a.label.localeCompare(b.label, "de"));
  const allOption: IOptionItem = { id: ALL_COURTS_OPTION_ID, label: t("common.allCourts") };
  const options = includeAllOption ? [allOption, ...courtOptions] : courtOptions;

  return <FormikAutocompleteSelect {...rest} options={options} />;
}
